/**
 * Shared helpers for shift-type codes.
 *
 * The lookup control table exposes direct-report-only variants (e.g. `SFT-D`,
 * `VTO-D`) as first-class shift types. The UI hides that distinction from users
 * by displaying the base type label everywhere; the `-D` variant only affects
 * server-side visibility filtering.
 */

/** Shift type codes that restrict visibility to the poster's direct reports. */
export const DIRECT_REPORT_SHIFT_TYPES = ['SFT-D', 'VTO-D'] as const;

/** Map from base shift type to its direct-report-only variant. */
const DIRECT_REPORT_TYPE_MAP: Record<string, string> = {
    SFT: 'SFT-D',
    VTO: 'VTO-D',
};

/**
 * Returns true when the given shift type code is a direct-report-only variant.
 */
export const isDirectReportShiftType = (shiftType?: string | null): boolean =>
    !!shiftType && (DIRECT_REPORT_SHIFT_TYPES as readonly string[]).includes(shiftType);

/**
 * Normalizes a shift type code to its base form (strips the direct-report
 * variant). Use for display, grouping, and any base-type comparisons.
 */
export const normalizeShiftType = (shiftType?: string | null): string => {
    if (!shiftType) return '';
    if (isDirectReportShiftType(shiftType)) {
        return shiftType.slice(0, -2);
    }
    return shiftType;
};

/**
 * Resolves the shift type code to persist based on the supervisor's
 * direct-report-only choice. Falls back to the base type when no variant
 * exists.
 */
export const resolveRequestType = (baseType: string, directReportOnly: boolean): string => {
    if (!directReportOnly) return baseType;
    return DIRECT_REPORT_TYPE_MAP[baseType] ?? baseType;
};
